import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/cn';
import { Spinner } from '@/components/UI/Spinner';

/**
 * RoofVox buttons: fully rounded pills, Poppins SemiBold.
 *   primary     orange call to action. Hover slides the fill to an orange -> violet gradient with a soft glow.
 *   accent      violet, for AI / SAM actions.
 *   secondary   outlined pill; the border turns into the orange -> violet gradient on hover.
 *   outline     neutral outline (cancel, dismiss, filters).
 *   ghost       low emphasis surface on hover.
 *   link        tertiary text, e.g. "Learn more".
 *   destructive irreversible or dangerous actions.
 * Only one primary per view: the hierarchy is the point.
 */
const buttonVariants = cva(
  // touch-manipulation removes the 300ms tap delay. motion-safe: the hover/press scale is dropped for
  // people who ask for reduced motion (index.css also zeroes transitions for them).
  'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-full font-poppins font-semibold touch-manipulation ' +
    'transition-[background-color,border-color,color,box-shadow,transform] duration-150 ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ui ' +
    'motion-safe:active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none',
  {
    variants: {
      variant: {
        primary: 'btn-sheen text-on-primary motion-safe:hover:scale-[1.02]',
        accent: 'bg-accent-fill text-on-accent hover:shadow-glow-accent hover:brightness-110 motion-safe:hover:scale-[1.02]',
        secondary:
          'border border-ui-border3 bg-transparent text-ui-text hover:border-sheen hover:bg-ui-card hover:text-ui-text',
        outline: 'border border-ui-border bg-ui-card text-ui-text hover:border-ui-border3 hover:bg-ui-hover',
        ghost: 'text-ui-text2 hover:bg-ui-hover hover:text-ui-text',
        link: 'rounded-md px-1 text-ui-text2 underline-offset-4 hover:text-ui-text hover:underline',
        destructive: 'bg-danger-fill text-white hover:brightness-110',
        // Tinted, low-emphasis actions inside cards ("Add", "Edit", "Open"): accent tint at rest, firmer on hover.
        soft: 'bg-accent-soft text-accent-text hover:brightness-95',
        // Quiet destructive action ("Remove", "Delete") that should not shout until hovered.
        'destructive-ghost': 'text-danger-text hover:bg-danger/10',
      },
      size: {
        // The 44px minimum touch target is enforced on coarse pointers (phones, tablets) rather than by
        // screen width, so a small desktop window keeps the compact controls.
        // `xs` is for dense workspace cards; it grows to 36px on touch instead of 44px so a card row stays usable.
        xs: 'h-6 px-2.5 text-xs [@media(pointer:coarse)]:h-9',
        sm: 'h-8 px-3.5 text-xs [@media(pointer:coarse)]:h-11',
        md: 'h-10 px-5 text-sm [@media(pointer:coarse)]:h-11',
        lg: 'h-12 px-7 text-sm',
        icon: 'h-10 w-10 p-0 text-sm [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, children, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size }), variant === 'link' && 'h-auto', className)}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading && <Spinner size="sm" className="-ml-1 text-current" />}
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';

export { Button, buttonVariants };
